import { Errors, FileValidator } from './file-validator';
import { FormWidget } from '../form-widget';
import { sendCustomError } from '@otto-ec/global-resources/apm';

export type InitFileUploadParams = {
    formElement: Pick<HTMLElement, 'querySelector'>;
};

export default class FileUpload extends FormWidget {
    private readonly ERROR_MESSAGES = {
        FILE_LIMIT_EXCEEDED:
            'Du kannst nicht mehr als <strong>5 Bilder</strong> mit einer <strong>Gesamtgröße von maximal 15 MB</strong> hinzufügen.',
        INVALID_FILE_SIZE: 'Du hast ein Bild ausgewählt, das keine gültige Dateigröße hat. Bitte wähle eine größere Datei aus.',
        INVALID_FILE_TYPE:
            'Bitte überprüfe das verwendete <strong>Dateiformat</strong> und ob du mehr als <strong>5 Bilder</strong> mit einer <strong>Gesamtgröße von maximal 15 MB</strong> hochgeladen hast.</span>',
    };

    private readonly MAX_FILES = 5;

    public name = 'file-upload';

    private readonly rootElement: HTMLFieldSetElement | null;
    private readonly fileInputElement: HTMLInputElement | null | undefined;
    private readonly previewContainerElement: HTMLDivElement | null | undefined;
    private readonly errorBannerElement: HTMLOcBannerV1Element | null | undefined;
    private readonly uploadButtonElement: HTMLLabelElement | null | undefined;

    private validationErrors: Set<Errors> = new Set<Errors>();
    private fileDataTransfer = new DataTransfer();

    private constructor(formElement: Pick<HTMLElement, 'querySelector'>) {
        super();

        this.rootElement = formElement.querySelector('.js_cr_file-upload');
        if (this.rootElement) {
            this.fileInputElement = this.rootElement.querySelector<HTMLInputElement>('.js_cr_file-upload__input');
            this.previewContainerElement = this.rootElement.querySelector<HTMLDivElement>('.js_cr_file-upload__preview');
            this.errorBannerElement = this.rootElement.querySelector<HTMLOcBannerV1Element>('.js_cr_file-upload__error-banner');
            this.uploadButtonElement = this.rootElement.querySelector<HTMLLabelElement>('.js_cr_file-upload__button');

            this.fileInputElement?.addEventListener('change', async event => {
                event.preventDefault();
                try {
                    await this.handleFileSelection();
                } catch (err) {
                    sendCustomError('fileUpload', new Error(err as string));
                }
            });
        }
    }

    private async handleFileSelection(): Promise<void> {
        await this.addNewFilesToCollection();

        const currentFiles = Array.from(this.fileDataTransfer.files);
        this.validationErrors = FileValidator.getOverallErrors(currentFiles);

        this.displayFilePreview(currentFiles);
        this.updateErrorDisplay();
        this.updateUploadButtonState(currentFiles.length >= this.MAX_FILES);

        document.dispatchEvent(new Event('filelist:updated'));
    }

    private async addNewFilesToCollection(): Promise<void> {
        if (!this.fileInputElement?.files) return;

        const selectedFiles = Array.from(this.fileInputElement.files);

        for (const file of selectedFiles) {
            this.fileDataTransfer.items.add(file);
        }

        this.fileInputElement.files = this.fileDataTransfer.files;
    }

    private displayFilePreview(files: File[]): void {
        if (!this.previewContainerElement) return;

        this.previewContainerElement.innerHTML = '';

        if (files.length === 0) return;

        const fileListElement = document.createElement('ul');
        files.forEach((file, index) => {
            const isFileValid = FileValidator.isValid(file);
            const listItemElement = this.createFilePreviewItem(file, index, isFileValid);
            fileListElement.appendChild(listItemElement);
        });

        this.previewContainerElement.appendChild(fileListElement);
    }

    private createFilePreviewItem(file: File, fileIndex: number, isValidFile: boolean): HTMLLIElement {
        const listItem = document.createElement('li');

        /*                             */
        const imageContainer = this.createImagePreviewContainer(file, isValidFile);
        listItem.appendChild(imageContainer);

        /*                       */
        const fileInfoSection = this.createFileInfoSection(file, isValidFile);
        listItem.appendChild(fileInfoSection);

        /*                   */
        const deleteButton = this.createDeleteButton(fileIndex);
        listItem.appendChild(deleteButton);

        return listItem;
    }

    private createImagePreviewContainer(file: File, isValidFile: boolean): HTMLDivElement {
        const imageContainer = document.createElement('div');
        imageContainer.classList.add('cr_file-upload__preview-image-frame');

        if (isValidFile) {
            const imageElement = new Image();
            imageElement.classList.add('cr_file-upload__preview-img');
            imageElement.src = URL.createObjectURL(file);
            imageContainer.appendChild(imageElement);
        }

        return imageContainer;
    }

    private createFileInfoSection(file: File, isValidFile: boolean): HTMLDivElement {
        const fileInfoContainer = document.createElement('div');
        fileInfoContainer.className = `cr_file-upload__preview-file-info${!isValidFile ? ' cr_file-upload__preview-file-info--invalid' : ''}`;

        fileInfoContainer.innerHTML = `
            <div class="cr_file-upload__preview-file-name">${file.name}</div>
            <div>${this.formatFileSizeInMB(file.size)}</div>
        `;

        return fileInfoContainer;
    }

    private createDeleteButton(fileIndex: number): HTMLOcIconButtonV2Element {
        const deleteButton = document.createElement('oc-icon-button-v2');
        deleteButton.icon = 'delete';
        deleteButton.ocAriaLabel = 'Bild löschen';
        deleteButton.size = '100';

        deleteButton.addEventListener('click', () => {
            this.removeFileAtIndex(fileIndex);
        });

        return deleteButton;
    }

    private removeFileAtIndex(fileIndex: number): void {
        this.fileDataTransfer.items.remove(fileIndex);

        if (this.fileInputElement) {
            this.fileInputElement.files = this.fileDataTransfer.files;
        }

        /*                                     */
        const remainingFiles = Array.from(this.fileDataTransfer.files);
        this.validationErrors = FileValidator.getOverallErrors(remainingFiles);
        this.displayFilePreview(remainingFiles);
        this.updateErrorDisplay();
        this.updateUploadButtonState(remainingFiles.length >= this.MAX_FILES);
    }

    private formatFileSizeInMB(sizeInBytes: number): string {
        const BYTES_PER_MB = 1024 * 1024;
        const sizeInMB = sizeInBytes / BYTES_PER_MB;
        const formattedSize = sizeInMB.toFixed(2).replace('.', ',') + ' MB';

        /*                                           */
        return formattedSize === '0,00 MB' ? '0,01 MB' : formattedSize;
    }

    private updateErrorDisplay(): void {
        if (!this.errorBannerElement) return;

        if (this.validationErrors.size > 0) {
            this.errorBannerElement.innerHTML = this.getErrorMessage(this.validationErrors);
            this.errorBannerElement.hidden = false;
            this.scrollIntoErrorView();
        } else {
            this.errorBannerElement.hidden = true;
            this.errorBannerElement.innerHTML = '';
        }
    }

    private getErrorMessage(errorSet: Set<Errors>): string {
        if (errorSet.has(Errors.INVALID_FILE_SIZE_FOUND)) {
            return this.ERROR_MESSAGES.INVALID_FILE_SIZE;
        }

        if (errorSet.has(Errors.INVALID_FILE_TYPE_FOUND)) {
            return this.ERROR_MESSAGES.INVALID_FILE_TYPE;
        }

        if (errorSet.has(Errors.FILE_COUNT_LIMIT_EXCEEDED) || errorSet.has(Errors.FILE_SIZE_LIMIT_EXCEEDED)) {
            return this.ERROR_MESSAGES.FILE_LIMIT_EXCEEDED;
        }

        return '';
    }

    private updateUploadButtonState(shouldDisable: boolean): void {
        this.uploadButtonElement?.classList.toggle('cr_file-upload__button--disabled', shouldDisable);
    }

    public isFormValid(): boolean {
        return this.validationErrors.size === 0;
    }

    public scrollIntoErrorView(): void {
        this.errorBannerElement?.scrollIntoView({ behavior: 'smooth' });
    }

    public static initialize({ formElement }: InitFileUploadParams): FileUpload {
        return new FileUpload(formElement);
    }
}
